import { SharedInsight, Trending, UsedInsight } from '@microsoft/microsoft-graph-types';
import { isAfter, parseISO } from 'date-fns';

export class MyDocument {
  public Title: string;
  public IconClasses: string;
  public Path: string;
  public EdgeAction: number;
  public EdgeDate: Date;
  public LastModified: Date;
  public Rank: number;
  public SharedBy: string;
  public ContainerUrl: string;

  constructor(opt?: Partial<MyDocument>) {
    Object.assign(this, opt);
  }

  static fromSharedInsight(val: SharedInsight, versionIE) {
    let path = val.resourceReference.webUrl ? val.resourceReference.webUrl : val.resourceVisualization.containerWebUrl;
    path = val.resourceVisualization.type === 'Web' && path !== null ? MyDocument.getWebUrl(path, val.resourceVisualization.title) : path;

    return new MyDocument({
      Title: versionIE > 0 ? MyDocument.getIETitle(val.resourceVisualization.title) : val.resourceVisualization.title,
      Path: path,
      IconClasses: MyDocument.getDocumentIcon(val.resourceVisualization.type),
      EdgeAction: 3,
      SharedBy: val.lastShared.sharedBy.displayName,
      ContainerUrl: val.resourceVisualization.containerWebUrl
    });
  }

  static fromTrendingDocuments(val: Trending, versionIE) {
    let path = val.resourceReference.webUrl ? val.resourceReference.webUrl : val.resourceVisualization.containerWebUrl;
    path = val.resourceVisualization.type === 'Web' && path !== null ? this.getWebUrl(path, val.resourceVisualization.title) : path;

    return new MyDocument({
      Title: versionIE > 0 ? this.getIETitle(val.resourceVisualization.title) : val.resourceVisualization.title,
      Path: path,
      IconClasses: this.getDocumentIcon(val.resourceVisualization.type),
      EdgeAction: 3,
      Rank: val.weight,
      ContainerUrl: val.resourceVisualization.containerWebUrl
    });
  }

  static fromUsedInsight(val: UsedInsight, versionIE): MyDocument {
    let path = val.resourceReference.webUrl ? val.resourceReference.webUrl : val.resourceVisualization.containerWebUrl;
    path = val.resourceVisualization.type === 'Web' && path !== null ? this.getWebUrl(path, val.resourceVisualization.title) : path;

    const lastModifiedDateTime = parseISO(val.lastUsed.lastModifiedDateTime);
    const lastAccessedDateTime = parseISO(val.lastUsed.lastAccessedDateTime);

    return new MyDocument({
      Title: versionIE > 0 ? this.getIETitle(val.resourceVisualization.title) : val.resourceVisualization.title,
      Path: path,
      IconClasses: MyDocument.getDocumentIcon(val.resourceVisualization.type),
      EdgeAction: isAfter(lastAccessedDateTime, lastModifiedDateTime) ? 1 : 2,
      EdgeDate: lastAccessedDateTime,
      LastModified: lastModifiedDateTime,
      ContainerUrl: val.resourceVisualization.containerWebUrl
    });
  }

  /*
 * Matches the file type of the file search result to a specfic icon.
 */
  static getDocumentIcon(fileType: string): string {
    let iconClasses = 'ms-Icon ';

    switch (fileType.toLowerCase()) {
      case 'pdf':
        iconClasses += 'ms-Icon--PDF';
        break;
      case 'word':
        iconClasses += 'ms-Icon--WordDocument';
        break;
      case 'excel':
        iconClasses += 'ms-Icon--ExcelDocument';
        break;
      case 'powerpoint':
        iconClasses += 'ms-Icon--PowerPointDocument';
        break;
      case 'onenote':
        iconClasses += 'ms-Icon--OneNoteLogo';
        break;
      case 'visio':
        iconClasses += 'ms-Icon--VisioLogo';
        break;
      default:
        iconClasses += 'ms-Icon--TextDocument';
        break;
    }

    return iconClasses;
  }

  /*
   * IE fix for hypenations where the title of the file is one long word and IE can't hypenate correctly.
   */
  static getIETitle(title: string): string {

    let trimmedTitle: string;
    const words = title.split(' ');

    if (words[0].length > 17) {
      words[0] = words[0].substring(0, 14);
      trimmedTitle = words[0] + '...';
    } else {
      trimmedTitle = title;
    }
    return trimmedTitle;
  }

  /*
   * Assemble a complete link to a page using the metadata provided in the object.
   */
  static getWebUrl(path: string, filename: string): string {
    let webFilePath: string;
    const index = path.indexOf('/Forms/');
    webFilePath = path.substring(0, index + 1);
    webFilePath += filename + '.aspx';
    return webFilePath;
  }
}
